import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Dialog } from './Dialog';

export type ConfirmOptions = {
  title: string;
  message: ReactNode;
  /** Primary button label. Default "Confirm". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Danger styling for destructive confirms. */
  tone?: 'default' | 'danger';
};

type ConfirmContextValue = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
};

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

let externalConfirm: ConfirmContextValue['confirm'] | null = null;

/** Imperative confirm — resolves true if user confirms. */
export function confirm(options: ConfirmOptions): Promise<boolean> {
  if (!externalConfirm) {
    console.warn('confirm() called outside ConfirmProvider');
    return Promise.resolve(false);
  }
  return externalConfirm(options);
}

type Pending = ConfirmOptions & {
  resolve: (value: boolean) => void;
};

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const queue = useRef<Pending[]>([]);

  const flush = useCallback(() => {
    setPending(queue.current[0] ?? null);
  }, []);

  const confirmFn = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>(resolve => {
        queue.current.push({ ...options, resolve });
        if (queue.current.length === 1) flush();
      }),
    [flush],
  );

  externalConfirm = confirmFn;

  const close = (result: boolean) => {
    const current = queue.current.shift();
    current?.resolve(result);
    flush();
  };

  const value = useMemo(() => ({ confirm: confirmFn }), [confirmFn]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Dialog
        open={pending != null}
        title={pending?.title ?? ''}
        onClose={() => close(false)}
        secondaryAction={{
          label: pending?.cancelLabel ?? 'Cancel',
          onClick: () => close(false),
        }}
        primaryAction={{
          label: pending?.confirmLabel ?? 'Confirm',
          variant: pending?.tone === 'danger' ? 'danger' : 'primary',
          onClick: () => close(true),
        }}
      >
        {pending?.message}
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmContextValue {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within ConfirmProvider');
  }
  return ctx;
}
